"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import axios from "axios";
import { useAuthStore } from "@/store/auth";

interface HotKeyword {
  keyword_id: number;
  keyword: string;
  score: number;
  normalized_score?: number | null;
  lifecycle_stage: string | null;
  category: string | null;
  mention_count: number;
}
interface WelcomeOut {
  seen: boolean;
  bonus_images_remaining: number;
  hot_keyword: HotKeyword | null;
  is_first_login: boolean;
}

const STAGE_LABEL: Record<string, string> = {
  emerging: "🌱 태동기",
  growing: "📈 성장기",
  mature: "🌳 성숙기",
  declining: "📉 하락기",
};

export function WelcomeModal() {
  const { token, isLoggedIn } = useAuthStore();
  const [data, setData] = useState<WelcomeOut | null>(null);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);  // 0: 환영, 1: 트렌드, 2: 보너스, 3: 매장 등록
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!token || !isLoggedIn) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get<WelcomeOut>("/api/me/welcome", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setData(r.data);
        // 모달 표시 조건: 이미 안 봤음 (seen=false). is_first_login 은 참고용
        if (!r.data.seen) setOpen(true);
      } catch {
        // 비로그인 / 만료 토큰은 무시
      }
    })();
    return () => { cancelled = true; };
  }, [token, isLoggedIn]);

  const dismiss = async () => {
    if (dismissing) return;
    setDismissing(true);
    try {
      if (token) {
        await axios.post(
          "/api/me/welcome/dismiss",
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        );
      }
    } catch {
      // 닫기 실패해도 UI 닫음 (멱등 + 다음에 다시 시도 가능)
    } finally {
      setOpen(false);
    }
  };

  if (!open || !data) return null;

  const total = 4;
  const hot = data.hot_keyword;
  const bonus = data.bonus_images_remaining;

  return (
    <div className="fixed inset-0 bg-black/70 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-bg w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl flex flex-col max-h-[92vh] overflow-hidden">
        {/* 진행 점 + 닫기 */}
        <div className="flex items-center justify-between px-5 pt-4 pb-2 shrink-0">
          <div className="flex gap-1.5">
            {Array.from({ length: total }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === step ? "w-6 bg-primary" : i < step ? "w-1.5 bg-primary/50" : "w-1.5 bg-border"
                }`}
              />
            ))}
          </div>
          <button
            onClick={dismiss}
            className="text-text-sub hover:text-text text-sm"
            aria-label="닫기"
          >
            건너뛰기 ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 min-h-0">
          {/* Step 0 — 환영 */}
          {step === 0 && (
            <div className="text-center py-4">
              <div className="text-5xl mb-3">👋</div>
              <h2 className="text-xl font-black text-text mb-2">
                트렌드레인에 오신 걸 환영합니다
              </h2>
              <p className="text-sm text-text-sub leading-relaxed">
                카페·외식 사장님을 위한 <span className="text-text font-semibold">트렌드 + 매장 운영 도구</span>
              </p>
              <div className="mt-5 space-y-2 text-left bg-bg-elevated rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <span className="text-base">🔥</span>
                  <p className="text-xs text-text">
                    <span className="font-bold">매일 자동 수집</span>되는 카페·F&amp;B 트렌드 키워드
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-base">🏪</span>
                  <p className="text-xs text-text">
                    <span className="font-bold">매장 운영</span> — 메뉴·재고·매출 + 영수증 사진 한 장 입력
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-base">🎨</span>
                  <p className="text-xs text-text">
                    <span className="font-bold">AI 이미지</span> — 메뉴 사진을 인스타·메뉴판용으로 변환
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 1 — 오늘의 핫 트렌드 */}
          {step === 1 && (
            <div>
              <div className="text-center mb-3">
                <div className="text-4xl mb-2">🔥</div>
                <h2 className="text-lg font-black text-text">오늘의 핫 트렌드</h2>
                <p className="text-2xs text-text-sub mt-1">
                  지금 가장 화제인 카페·F&amp;B 키워드
                </p>
              </div>
              {hot ? (
                <Link
                  href={`/trend/${hot.keyword_id}`}
                  onClick={dismiss}
                  className="block card p-4 bg-primary/5 border-primary/30 hover:bg-primary/10 transition-colors"
                >
                  <div className="flex items-center gap-2 mb-2">
                    {hot.lifecycle_stage && (
                      <span className="text-2xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                        {STAGE_LABEL[hot.lifecycle_stage] || hot.lifecycle_stage}
                      </span>
                    )}
                    {hot.category && (
                      <span className="text-2xs text-text-sub">{hot.category}</span>
                    )}
                  </div>
                  <p className="text-2xl font-black text-text break-keep">{hot.keyword}</p>
                  {/* 룰 v4 (2026-05-07): 점수 X, 30일 멘션 영향력 표시 */}
                  <div className="mt-3 flex items-end justify-end">
                    <div className="text-right">
                      <p className="text-2xs text-text-sub">최근 30일 언급</p>
                      <p className="text-lg font-black text-primary">
                        {hot.mention_count.toLocaleString()}
                        <span className="text-2xs text-text-sub ml-1">건</span>
                      </p>
                    </div>
                  </div>
                  <p className="text-2xs text-primary mt-3 text-right">상세 보기 →</p>
                </Link>
              ) : (
                <div className="card p-6 text-center">
                  <p className="text-sm text-text-sub">오늘자 트렌드가 아직 집계되지 않았어요</p>
                  <p className="text-2xs text-text-sub mt-1">매일 자정에 자동 수집됩니다</p>
                </div>
              )}
              <p className="text-2xs text-text-sub mt-3 text-center">
                💡 트렌드는 매일 자정에 자동 갱신
              </p>
            </div>
          )}

          {/* Step 2 — AI 이미지 가입 보너스 */}
          {step === 2 && (
            <div className="text-center py-2">
              <div className="text-5xl mb-3">🎁</div>
              <h2 className="text-xl font-black text-text mb-1">
                가입 축하 — AI 이미지 1매 무료
              </h2>
              <p className="text-sm text-text-sub leading-relaxed">
                매장 메뉴 사진을 <span className="text-text font-semibold">인스타·메뉴판·POP</span> 용으로
                <br />
                즉시 변환해드려요
              </p>

              <div className="mt-5 mb-3 card bg-primary/5 border-primary/30 p-4">
                <p className="text-2xs text-text-sub mb-1">현재 보너스 잔여</p>
                <p className="text-3xl font-black text-primary">
                  {bonus}<span className="text-sm font-normal text-text-sub ml-1">매</span>
                </p>
                <p className="text-2xs text-text-sub mt-2">
                  ※ 매장 등록 없이도 사용 가능 · 한 번 쓰면 끝
                </p>
              </div>

              {bonus > 0 ? (
                <Link
                  href="/image-studio"
                  onClick={dismiss}
                  className="block w-full btn-primary text-sm py-3 font-bold"
                >
                  🎨 지금 만들어보기 →
                </Link>
              ) : (
                <div className="text-2xs text-text-sub py-3">
                  보너스를 이미 사용하셨어요. lite/pro 구독 시 월 한도 부여됩니다
                </div>
              )}
            </div>
          )}

          {/* Step 3 — 매장 등록 (Phase 1.4a, 2026-05-12) — funnel 개선용 락인 CTA */}
          {step === 3 && (
            <div className="text-center py-2">
              <div className="text-5xl mb-3">🏪</div>
              <h2 className="text-xl font-black text-text mb-1">
                매장 등록하고 내 데이터 자동 분석
              </h2>
              <p className="text-sm text-text-sub leading-relaxed">
                트렌드만 보는 건 50% — 내 매장 데이터까지 합쳐야
                <br />
                <span className="text-text font-semibold">진짜 인사이트</span> 가 나옵니다
              </p>

              <div className="mt-5 mb-4 card bg-primary/5 border-primary/30 p-4 text-left space-y-2.5">
                <div className="flex items-start gap-2">
                  <span className="text-base shrink-0">📊</span>
                  <p className="text-xs text-text leading-relaxed">
                    <span className="font-bold">월별 손익 자동</span> — 매출·매출원가·총이익·마진율 한눈에
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-base shrink-0">🏆</span>
                  <p className="text-xs text-text leading-relaxed">
                    <span className="font-bold">메뉴 수익 순위</span> — 진짜 잘 팔리는 메뉴 + 4주 변동
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-base shrink-0">📷</span>
                  <p className="text-xs text-text leading-relaxed">
                    <span className="font-bold">영수증 사진 한 장</span> 으로 판매·재고 자동 입력
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-base shrink-0">🔥</span>
                  <p className="text-xs text-text leading-relaxed">
                    <span className="font-bold">동네 갭 분석</span> — 내 메뉴 vs 동네 인기 트렌드
                  </p>
                </div>
              </div>

              <Link
                href="/store/setup"
                onClick={dismiss}
                className="block w-full btn-primary text-sm py-3 font-bold"
              >
                🏪 지금 매장 등록 →
              </Link>
              <p className="text-2xs text-text-sub mt-2">
                3분이면 끝 · 언제든 추가·수정 가능
              </p>
            </div>
          )}
        </div>

        {/* 하단 네비 */}
        <div className="border-t border-border bg-bg p-3 flex items-center gap-2 shrink-0">
          {step > 0 && (
            <button
              onClick={() => setStep(step - 1)}
              className="text-xs px-4 py-2.5 border border-border rounded text-text-sub"
            >
              ← 이전
            </button>
          )}
          {step < total - 1 ? (
            <button
              onClick={() => setStep(step + 1)}
              className="flex-1 text-sm py-2.5 bg-primary text-bg rounded font-bold"
            >
              다음 →
            </button>
          ) : (
            <button
              onClick={dismiss}
              disabled={dismissing}
              className="flex-1 text-sm py-2.5 border border-border rounded text-text-sub disabled:opacity-50"
            >
              {dismissing ? "닫는 중..." : "나중에 등록"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
